import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";
import IOsservazione from "../../types/IOsservazione";

interface OsservazioneState {
  agenda: IOsservazione[];
  selectedOsservazione: IOsservazione | null;
  loading: boolean;
  error: string | null;
}

const initialState: OsservazioneState = {
  agenda: [],
  selectedOsservazione: null,
  loading: false,
  error: null,
};

// Fetch all agenda
export const fetchAgenda = createAsyncThunk(
  "agenda/fetchAgenda",
  async ({ id }: { id: number }) => {
    const response = await axiosInstance.get(`/agenda/${id}`);
    return response.data;
  }
);

// Fetch a single osservazione by data
export const fetchOsservazione = createAsyncThunk(
  "agenda/fetchOsservazione",
  async (
    { id, anno, num }: { id: number; anno: string; num: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(`/agenda/${id}/${anno}/${num}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ??
          "Errore durante il recupero dell'osservazione"
      );
    }
  }
);

// Create a new osservazione
export const createOsservazione = createAsyncThunk(
  "agenda/createOsservazione",
  async (newOsservazione: IOsservazione, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/agenda/${newOsservazione.Id_Anagrafica}/${newOsservazione.Anno}/${newOsservazione.Num}`,
        newOsservazione
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ??
          "Errore durante la creazione dell'osservazione"
      );
    }
  }
);

// Delete an existing osservazione
export const deleteOsservazione = createAsyncThunk(
  "agenda/deleteOsservazione",
  async ({ id, anno, num }: { id: number; anno: string; num: number }) => {
    const response = await axiosInstance.delete(`/agenda/${id}/${anno}/${num}`);
    return { ...response.data, Anno_Osservazione: anno, Num_Osservazione: num };
  }
);

export const downloadPdfOsservazione = async ({
  id,
  anno,
  num,
}: {
  id: number;
  anno: string;
  num: number;
}) => {
  try {
    const response = await axiosInstance.get(
      `/agenda/${id}/${anno}/${num}/download`,
      {
        responseType: "blob", // Importante per ottenere il PDF come file binario
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ?? "Errore durante il download del PDF"
    );
  }
};

export const downloadZipStorico = async ({ id }: { id: number }) => {
  try {
    const response = await axiosInstance.get(`/agenda/${id}/download`, {
      responseType: "blob", // Importante per ottenere il file ZIP come file binario
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ?? "Errore durante il download del file ZIP"
    );
  }
};

const agendaSlice = createSlice({
  name: "agenda",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all agenda
      .addCase(fetchAgenda.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgenda.fulfilled, (state, action) => {
        state.loading = false;
        state.agenda = action.payload;
      })
      .addCase(fetchAgenda.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch agenda";
      })
      // Fetch a single osservazione by data
      .addCase(fetchOsservazione.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOsservazione.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOsservazione = action.payload;
      })
      .addCase(fetchOsservazione.rejected, (state, action) => {
        state.loading = false;
        state.selectedOsservazione = null;
        state.error =
          (action.payload as string) || "Failed to fetch the osservazione";
      })
      //* Create a new osservazione
      .addCase(createOsservazione.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOsservazione.fulfilled, (state, action) => {
        state.loading = false;
        state.agenda.push(action.payload);
        state.selectedOsservazione = action.payload;
      })
      .addCase(createOsservazione.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to create osservazione";
      })
      //* Delete an existing osservazione
      .addCase(deleteOsservazione.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOsservazione.fulfilled, (state, action) => {
        state.loading = false;
        state.agenda = state.agenda.filter(
          (osservazione) =>
            osservazione.Anno !== action.payload.Anno_Osservazione ||
            osservazione.Num !== action.payload.Num_Osservazione
        );
        state.selectedOsservazione = null;
      })
      .addCase(deleteOsservazione.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to delete osservazione";
      });
  },
});

export default agendaSlice.reducer;
